import { debounce } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import { CircularProgress } from '@material-ui/core';
import { useUserContext as useBaseUserContext } from '@sierralabs/user-ui';

import { useGetCurrentUser } from './User.hooks';
import api from '../common/api/api';
import environment from '../environments';
import { User } from './User.interface';
import styled from 'styled-components';
import { getUserPermissions, useUserPermissions } from './useUserPermissions';

const EXPIRE_TIME_KEY = 'expireTime';

const StyledSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

// make sure logout and expiration clear out cookies
// to avoid issues where refreshing the page logs the user back in
export function clearAccessTokens() {
  api.removeAccessToken();
  api.removeAccessToken('atlassian');
  [
    'atlassian',
    'isJira',
    'jiraServerToken',
    'jiraServerTokenSecret',
    'jira'
  ].forEach(cookie => api.clearCookie(cookie));
}

function getExpireTime() {
  return parseInt(localStorage.getItem(EXPIRE_TIME_KEY) || '');
}

export type UserContextType = User | null | undefined;

export interface UserContextState {
  user?: UserContextType;
  hasRole: (role: string) => boolean;
  didSessionExpire?: boolean;
  permissions: ReturnType<typeof useUserPermissions>;
  resetSessionTime: () => void;
  setUser: React.Dispatch<React.SetStateAction<UserContextType>>;
}

export const UserContext = createContext<UserContextState>({
  user: undefined,
  setUser: () => {},
  didSessionExpire: false,
  resetSessionTime: () => {},
  hasRole: () => false,
  permissions: getUserPermissions([])
});

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      'useUserContext must be used within the UserProvider component'
    );
  }
  return context;
};

interface UserProviderProps {
  children: JSX.Element;
}

export default function UserProvider(props: UserProviderProps): JSX.Element {
  const { children } = props;
  const { setUser: setBaseUser } = useBaseUserContext();
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [userContext, setUser] = useState<UserContextType>(undefined);
  const permissions = useUserPermissions(userContext);
  const { user, get } = useGetCurrentUser();
  const [sessionExpired, setSessionExpired] = useState(false);
  const accessToken = api.getAccessToken();
  const hasUser = !!userContext;

  const updateExpireTime = useCallback(() => {
    // handle no timeout logic
    if (environment.timeout === 0) {
      return;
    }
    const mins = environment.timeout || 15;
    localStorage.setItem(
      EXPIRE_TIME_KEY,
      (Date.now() + mins * 60 * 1000).toString()
    );
  }, []);

  const handleListeners = useCallback((listener: () => void) => {
    // events that will reset expireTime
    const events = ['mousemove', 'scroll', 'keydown'];
    // handle all scroll events
    // NOTE: taken from https://stackoverflow.com/a/30723677/7013346
    document.addEventListener('scroll', listener, true);
    events.forEach(event => window.addEventListener(event, listener));
    return () => {
      document.removeEventListener('scroll', listener, true);
      events.forEach(event => window.removeEventListener(event, listener));
    };
  }, []);

  // create timer loop with ms = expireTime - now
  const createTimer = useCallback((callback: () => void) => {
    const expireTime = getExpireTime();
    const ms = isFinite(expireTime) ? expireTime - Date.now() : undefined;
    if (typeof ms !== 'undefined') {
      console.debug('[session] next check: %dms', ms);
      timerRef.current = setTimeout(() => {
        const expireTime = getExpireTime();
        if (isFinite(expireTime) && expireTime < Date.now()) {
          callback();
        } else {
          createTimer(callback);
        }
      }, ms);
    }
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, []);

  // session implementation
  useEffect(() => {
    // avoid running session expiration logic when logged out
    if (!hasUser) {
      return;
    }
    console.debug('[session] set listeners');
    // avoid long debounce time since consecutive interactions may
    // not update the expire time and incorrectly evaluate expiration
    // e.g. constantly scrolling or moving cursor
    const clearListeners = handleListeners(debounce(updateExpireTime, 20));
    const clearTimer = createTimer(() => {
      console.debug('[session] expired');
      clearAccessTokens();
      setSessionExpired(true);
      setUser(null);
      clearListeners();
      // remove expireTime so that session expire message disappears on refresh
      localStorage.removeItem(EXPIRE_TIME_KEY);
    });
    return () => {
      console.debug('[session] clear');
      clearTimer();
      clearListeners();
    };
  }, [hasUser, createTimer, handleListeners, updateExpireTime]);

  useEffect(() => {
    if (accessToken) {
      get();
      setSessionExpired(false);
      updateExpireTime();
    } else {
      setUser(null);
    }
  }, [accessToken, updateExpireTime, get]);

  useEffect(() => {
    setUser(user);
  }, [setUser, user]);

  const hasRole = useCallback(
    (role: string) => {
      if (userContext?.roles) {
        return userContext.roles.some(r => r.name === role);
      }
      return false;
    },
    [userContext]
  );

  // NOTE: UserProviderRoot is not rendered until BaseUserProvider has a user
  // make sure to set userContext only if it exists
  useEffect(() => {
    userContext && setBaseUser(userContext as any);
  }, [userContext, setBaseUser]);

  if (userContext === undefined) {
    // Do not load the children of the provider if the user is undefined.
    // Lets further wait...
    // undefined = we have not determined the login state yet
    // null = not logged in
    // User value = logged in
    return (
      <StyledSpinnerContainer>
        <CircularProgress size={64} />
      </StyledSpinnerContainer>
    );
  }

  return (
    <UserContext.Provider
      value={{
        user: userContext,
        hasRole,
        permissions,
        didSessionExpire: sessionExpired,
        resetSessionTime: updateExpireTime,
        setUser
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
